import { API, unwrapData } from '../axios-instance'
import { LanguagesEnum } from '../identity'
import { PageResult, TranslationStatuses } from '../shared'
import { createBatchingRoutes } from '../shared/batch'
import {
  ArchiveAwardTypeParams,
  AwardSendBulkParams,
  AwardType,
  AwardTypeRecipient,
  AwardTypeTranslation,
  AwardTypeTranslationParams,
  CreateOrUpdateAwardTypeParams,
  CreateOrUpdateNominationTypeParams,
  GetAwardTypeParams,
  ListAwardTypeParams,
  ListAwardTypeRecipientsParams,
  MyAwardType,
  AwardsSummary,
  PreviewAwardTypeParams,
  PreviewAwardTypeResults,
} from './types'

export const createAwardType = async (params: CreateOrUpdateAwardTypeParams) => {
  return await API.post<AwardType>('/award/awardTypes', params).then(unwrapData)
}

export const updateAwardType = async (awardId: string, params: CreateOrUpdateAwardTypeParams) => {
  return await API.put<AwardType>(`/award/awardTypes/${awardId}`, params).then(unwrapData)
}

export const createNominationType = async (params: CreateOrUpdateNominationTypeParams) => {
  return await API.post<AwardType>('/award/awardTypes', params).then(unwrapData)
}

export const updateNominationType = async (
  nominationId: string,
  params: CreateOrUpdateNominationTypeParams,
) => {
  return await API.put<AwardType>(`/award/awardTypes/${nominationId}`, params).then(unwrapData)
}

export const listAwardTypes = async (params: ListAwardTypeParams) => {
  return await API.get<PageResult<AwardType>>('/award/awardTypes', { params }).then(unwrapData)
}

export const getAwardType = async (awardTypeId: string, params: GetAwardTypeParams) => {
  return await API.get<AwardType>(`/award/awardTypes/${awardTypeId}`, { params }).then(unwrapData)
}

export const listAwardTypeRecipients = async (
  awardTypeId: string,
  params: ListAwardTypeRecipientsParams,
) => {
  return await API.get<PageResult<AwardTypeRecipient>>(
    `/award/awardTypes/${awardTypeId}/recipients`,
    {
      params,
    },
  ).then(unwrapData)
}

export const exportAwardsTypeRecipientsCsv = async (
  awardTypeId: string,
  params: ListAwardTypeRecipientsParams,
) => {
  return await API.post<BlobPart>(`/award/awardTypes/${awardTypeId}/recipients/export`, params, {
    params: { sorting: params.sorting },
  }).then(unwrapData)
}

export const archiveAwardType = async (awardTypeId: string, params: ArchiveAwardTypeParams) => {
  return await API.patch<AwardType>(`/award/awardTypes/${awardTypeId}`, params).then(unwrapData)
}

export const cloneAwardType = async (awardTypeId: string) => {
  return await API.post<AwardType>(`/award/awardTypes/${awardTypeId}/clone`).then(unwrapData)
}

export const myAwardTypes = async (params: ListAwardTypeParams) => {
  return await API.get<PageResult<MyAwardType>>('/awards/awardTypes/my', { params }).then(
    unwrapData,
  )
}

export const previewAwardType = async (params: PreviewAwardTypeParams) => {
  return await API.post<PreviewAwardTypeResults>(
    `/awards/awardTypes/${params.awardTypeId}/preview`,
    params,
  ).then(unwrapData)
}

export const getAwardsSummary = async (memberId?: string) => {
  const path = memberId ? `/award/awardTypes/summary/${memberId}` : '/award/awardTypes/summary'
  return await API.get<AwardsSummary[]>(path).then(unwrapData)
}

//  delegation
export const listDelegatorAwards = async (delegatorId: string, params: ListAwardTypeParams) => {
  return await API.get<PageResult<AwardType>>(`/award/awardTypes/delegator/${delegatorId}`, {
    params,
  }).then(unwrapData)
}

export const listAwardTypeTranslations = async (language: LanguagesEnum) => {
  return await API.get<AwardTypeTranslation[]>(`/award/awardTypes/translations/${language}`).then(
    unwrapData,
  )
}

export const updateAwardTypeTranslation = async (
  language: LanguagesEnum,
  params: AwardTypeTranslationParams[],
) => {
  return await API.put<AwardTypeTranslation[]>(
    `/award/awardTypes/translations/${language}`,
    params,
  ).then(unwrapData)
}

export const getAwardTypeTranslationStatus = async () => {
  return await API.get<TranslationStatuses>('/award/awardTypes/translations/status').then(
    unwrapData,
  )
}

export const downloadBulkAwardSendCsvTemplate = async () => {
  return await API.get<BlobPart>('/award/awards/template', {
    responseType: 'blob',
  }).then(unwrapData)
}

export const batchSendAwardRoutes = createBatchingRoutes<AwardSendBulkParams>({
  baseUrl: '/award/awards/batchSend',
  init: {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  },
  update: {
    path: 'items',
  },
})
